/**
  * @class mmodOfficeFleet
  * @description
  * Fleet module displays online operational vehicles of the system.
  * It includes only those vehicles that the user has permissions to see
  * - i.e. user must have assigned role in the same zone as the vehicle application
  * and the role can not be PASSIVE.
  * Technically, it is just another view into Applications list,
  * similar to Units, however limited to mBox applications,
  * ignoring whether there is logged user or not.
  * There are two purposes for this module:
  * - operational: see what vehicles are in the operation - with ability to stream video data from it
  * - technical: verify installation and running of mBox service in vehicle
  * Module is implemented without workflow, as a pure module named mmodOfficeFleet.
  * It listens to mmApplications and mmAppGps messages which are sent to #APPS queue by mmodUnits module.
  * It also performs request to server’s msvrApplications listener
  * to obtain system information on selected vehicle using mmGetSysInfo message.
  * Module is used only in mOffice as it has no meaningful purpose for field officers.
  */

import mmodModule from 'mmodModule'

const geo = require('msysGeo')

export default class mmodOfficeFleet extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'full' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.applications_version = 0
    this.applications = {}
    this.app_gps = {}

    this.loadForm(require('./mmodOfficeFleet.json'))
    this.elList = this.el.find('>mele-body >@list')
    this.elDetail = this.el.find('>mele-body >@detail')
    this.elStreaming = this.el.find('>mele-body >@streaming')
    this.elPlayer = this.el.find('mele-player')
    this.elPlayer.routing = JSON.stringify({
      mmGps: ['speed'],
    })

    this.elList.eventSelected = (self, items, old, context_user_interaction)=>{
      if(this.elDetail) {
        this.el.views = 'list,detail'
        var key = items[0]
        this.checkButtons(key)
        var item = this.elList.getItem(key)
        this.elDetail.datakey = key
        this.elDetail.unavailable = _.isNil(key)
        if(this.elDetail.unavailable) this.elDetail.formClear()
        if(item) {
          this.elDetail.setValues(item.data)
          this.elDetail.scrollTop()
          if(context_user_interaction) {
            const idx = _.toString(this.el.views).split(',').indexOf(this.elDetail.key)
            if(idx>=0) this.el.active = idx
          }
        }else{
          this.elDetail.setValues()
        }
        this.publish(':msvrApplications', 'mmGetSysInfo', { id: items[0] })
      }
      if(this.elStreaming) {
        this.elPlayer.pause()
        this.elStreaming.datakey = key
        if(item) {
          this.elStreaming.setValues(item.data)
        }else{
          this.elStreaming.setValues()
        }
      }
    } // eventSelected

    this.mmStart()
    this.subscribe('#APPS')
    this.subscribe('#MAP')
    this.subscribe('#WM')
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server sent list of applications
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.list] - array of changed records
  */
  mmApplications(sender, params) {
    if(params.version >= this.applications_version) {
      this.applications_version = params.version
      if(params.full) this.applications = {}
      params.list.forEach(change=> {
        this.applications[change.id] = change
      })
      this.refreshList()
    }
  } // mmApplications

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information from server on location change of other application
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {array} params.list - array of changed records
  */
  mmAppGps(sender, params) {
    params.list.forEach(rec=>{
      this.app_gps[rec.id] = _.omit(rec, 'id')
      this.app_gps[rec.id].dt = Date.now()
    })
    this.refreshList()
  } // mmAppGps

  refreshList() {
    const vehicles = Object.values(this.applications)
      .filter(app=>{
        return app.apptype === 'mBox' && app.enabled
      })
      .map(mbox=>{
        const gps = this.app_gps[mbox.id] || {}
        const data = Object.assign({}, mbox, {
          geohash: gps.gps,
          gps: geo.toLatLng(gps.gps) || '',
          speed: gps.speed,
        })
        return {
          key: mbox.id,
          label: mbox.note,
          label2: mbox.id,
          disabled: !(mbox.enabled > 0),
          data: _.extend(data, {
            available: mbox.online > 0 ? 'gready' : 'bout',
          }),
        }
      })

    this.checkButtons(this.elDetail.datakey)
    this.elList.updateItems(vehicles)
    if(vehicles.length == 0) this.el.views = 'list,empty'
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    if(params.button=='refresh') {
      this.publish(':msvrApplications', 'mmGetSysInfo', { id: this.elDetail.datakey })
      this.elPlayer.pause()
    }else if(params.button=='track') {
      this.publish('msysWM', 'mmWindowFocus', { id: 'mmodMap' })
      this.publish('mmodMap', 'mmMapSelect', { type: 'unit', id: this.elDetail.datakey, zoom: 16 })
      this.elPlayer.pause()
    }else if(params.button=='streaming') {
      this.el.views = 'list,streaming'
      this.elPlayer.selected = ''
      this.elPlayer.source = this.elDetail.datakey+':msysStreamer'
      this.elPlayer.play(0)
    }else if(params.button=='reconnect') {
      this.elPlayer.selected = ''
      this.elPlayer.play(0)
    }else if(params.button=='overview') {
      this.elPlayer.selected = ''
    }else if(params.button=='details') {
      this.el.views = 'list,detail'
      this.elPlayer.pause()
    }
  } // mmButtonClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * start video streaming using mmodPlayer module
  */
  startStreaming() {
  } // startStreaming

  checkButtons(item) {
    this.elDetail.forAll('.mele-button@streaming', el=>{
      el.disabled = !(this.applications[item] && this.applications[item].online > 0)
    })
    this.elDetail.forAll('.mele-button@track', el=>{
      el.disabled = !this.app_gps[item]
    })
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * system information
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {object} [params.system]
  * @param {object} [params.cpu]
  * @param {number} [params.load]
  * @param {object} [params.mem]
  * @param {array} [params.fs]
  * @param {object} [params.proc]
  * @param {object} [params.osInfo]
  * @param {object} [params.networkInterfaces]
  * @param {object} [params.net]
  * @param {object} [params.users]
  * @param {object} [params.db]
  */
  mmSysInfo(sender, params) {
    // super.mmSysInfo(sender, params);
    const cpu = params.cpu
    const fs = params.fs
    const mem = params.mem
    const system = params.system
    const osInfo = params.osInfo
    const users = Object.values(params.users)
    const networkInterfaces = Object.values(params.networkInterfaces)

    this.elDetail.find('@cpubrand').value = `${cpu.manufacturer} ${cpu.brand} ${cpu.speed}GHz`
    this.elDetail.find('@cpucores').value = `${cpu.cores}`
    this.elDetail.find('@memtotal').value = `${Math.round(mem.total/1000000)}`
    this.elDetail.find('@memfree').value = `${Math.round(mem.free/100000000)}`
    this.elDetail.find('@memused').value = `${Math.round(mem.used/1000000)}`

    this.elDetail.find('@osdistrib').value = osInfo.distro
    this.elDetail.find('@osname').value = osInfo.codename
    this.elDetail.find('@osrelease').value = osInfo.release

    const fsGroup = this.elDetail.find('@filesystem')
    fsGroup.innerHTML = ''
    const elGroupsFs = []
    fs.forEach((x, i) => {
      const header = i == 0 ? this.el.translate('fs') : ''
      x.size = (x.size/1000000000).toFixed(2)
      x.used = (x.used/1000000000).toFixed(2)
      const group =
    {
      key: `fs_${i}`,
      tag: 'mele-group',
      type: 'string',
      propagate: true,
      class: 'hr',
      readonly: true,
      header,
      items: [
        { key: 'fs', tag: 'mele-input', type: 'string', label: 'fs.fs', width: '1/2', split: '1:2', value: x.fs },
        { key: 'mount', tag: 'mele-input', type: 'string', label: 'fs.mount', width: '1/2', split: '1:2', value: x.mount },
        { key: 'size', tag: 'mele-input', type: 'string', label: 'fs.size', width: '1/2', split: '1:2', after: 'Gb', value: x.size },
        { key: 'type', tag: 'mele-input', type: 'string', label: 'fs.type', width: '1/2', split: '1:2', value: x.type },
        { key: 'used', tag: 'mele-input', type: 'string', label: 'fs.used', width: '1/2', split: '1:2', after: 'Gb', value: x.used },
      ],
    }
      elGroupsFs.push(group)
    })
    fsGroup.appendChildren(elGroupsFs)

    const usersGroup = this.elDetail.find('@users')
    usersGroup.innerHTML = ''
    const elGroupsUsers = []
    users.forEach((x, i) => {
      const header = i == 0 ? this.el.translate('users') : ''
      const usersClass = i == 0 ? 'hr': ''
      const group =
    {
      key: `users_${i}`,
      tag: 'mele-group',
      type: 'string',
      propagate: true,
      class: usersClass,
      readonly: true,
      header,
      items: [
        { key: 'user', tag: 'mele-input', type: 'string', label: 'users.username', width: '1/2', split: '1:2', value: x.user },
        { key: 'tty', tag: 'mele-input', type: 'string', label: 'users.tty', width: '1/2', split: '1:2', value: x.tty },
      ],
    }
      elGroupsUsers.push(group)
    })
    usersGroup.appendChildren(elGroupsUsers)

    const networkInterfacesGroup = this.elDetail.find('@networkinterfaces')
    networkInterfacesGroup.innerHTML = ''
    const elGroupsNetworkInterfaces = []
    networkInterfaces.forEach((x, i) => {
      const header = i == 0 ? this.el.translate('networkinterfaces') : ''
      const group =
    {
      key: `ni_${i}`,
      tag: 'mele-group',
      type: 'string',
      propagate: true,
      class: 'hr',
      readonly: true,
      header,
      items: [
        { key: 'iface', tag: 'mele-input', type: 'string', label: 'ni.iface', width: '1/2', split: '1:2', value: x.iface },
        { key: 'type', tag: 'mele-input', type: 'string', label: 'ni.type', width: '1/2', split: '1:2', value: x.type },
        { key: 'ip4', tag: 'mele-input', type: 'string', label: 'ni.ip4', width: '1/2', split: '1:2', value: x.ip4 },
        { key: 'ip6', tag: 'mele-input', type: 'string', label: 'ni.ip6', width: '1/2', split: '1:2', value: x.ip6 },
        { key: 'operstate', tag: 'mele-input', type: 'string', label: 'ni.operstate', width: '1/2', split: '1:2', value: x.operstate },
      ],
    }
      elGroupsNetworkInterfaces.push(group)
    })
    networkInterfacesGroup.appendChildren(elGroupsNetworkInterfaces)
  } // mmSysInfo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  * @param {object} context - reply context
  */
  mmWindowFocused(sender, params, context) {
    if(params.id != this.listener_id && this.elPlayer) this.elPlayer.pause()
  } // mmWindowFocused
} // mmodOfficeFleet
